import { toast } from "sonner";
import { useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom"; // Import useNavigate and useParams
import { FormStep } from "@/components/listings/filters/FilterSchema";
import { useListingWizardContext } from "@/context/ListingWizardContext";
import { getFieldsForStep } from "../utils/formUtils";

export const useFormNavigation = () => {
  const {
    form,
    currentStep,
    setCurrentStep,
    setStepCompleted,
    canProceedToNextStep,
    setValidateOnChange,
    // setFormTouched, // No longer needed as formTouched is derived
    saveCurrentStep,
    listingId, // Get listingId from context
  } = useListingWizardContext();
  const navigate = useNavigate();
  const params = useParams<{ listingId?: string; step?: string }>();

  // Memoize handlers to prevent recreation on each render
  const handleNextStep = useCallback(async () => {
    console.log(
      "[useFormNavigation] handleNextStep called. Current step:",
      FormStep[currentStep]
    );
    // Only validate the form if the step hasn't been validated yet
    if (!canProceedToNextStep) {
      setValidateOnChange(true);

      const currentStepFields = getFieldsForStep(currentStep);
      console.log(
        "[useFormNavigation] Validating fields for current step:",
        currentStepFields
      );
      const isValid = await form.trigger(currentStepFields as any, {
        shouldFocus: true,
      });
      console.log(
        "[useFormNavigation] Validation result for current step:",
        isValid
      );

      if (!isValid) {
        toast.error("Please complete all required fields", {
          duration: 3000,
          position: "bottom-center",
        });
        return;
      }
    }

    try {
      console.log(
        "[useFormNavigation] Attempting to save current step data..."
      );
      // Call saveCurrentStep once at the beginning
      const savedDraftResult = await saveCurrentStep();
      console.log(
        "[useFormNavigation] saveCurrentStep result:",
        savedDraftResult
      );

      // Determine the listing ID for navigation
      // Prioritize the listingId from context, then the one returned by saveCurrentStep
      const currentDraftId = listingId || savedDraftResult?.listing?.id;
      console.log(
        "[useFormNavigation] Determined currentDraftId for navigation:",
        currentDraftId
      );

      const nextStep = (currentStep + 1) as FormStep;

      // Logic for navigating from Location to Media (or any step that requires a listingId)
      if (currentStep === FormStep.Location) {
        if (!currentDraftId) {
          console.error(
            "[useFormNavigation] Cannot navigate to Media step: Listing ID not available after saving Location."
          );
          toast.error(
            "Please complete the Location details to proceed to the Media step."
          );
          return; // Prevent navigation
        }
        // If currentDraftId exists, proceed to navigate to edit route
        setStepCompleted(currentStep, true);
        setCurrentStep(nextStep);
        setValidateOnChange(false);
        // setFormTouched(false); // No longer needed as formTouched is derived
        navigate(
          `/dashboard/add-listing/edit/${currentDraftId}/step/${nextStep}`,
          {
            replace: true,
          }
        );
      } else if (currentStep < FormStep.Location) {
        // For steps before Location, save locally and navigate to the 'new' route
        // The initial saveCurrentStep() already handled local storage.
        setStepCompleted(currentStep, true);
        setCurrentStep(nextStep);
        setValidateOnChange(false);
        // setFormTouched(false); // No longer needed as formTouched is derived
        navigate(`/dashboard/add-listing/new/step/${nextStep}`, {
          replace: true,
        });
      } else {
        // For steps after Location (including Media onwards), use the existing listingId
        if (!currentDraftId) {
          console.error(
            "[useFormNavigation] Critical: Listing ID not available for steps after Location."
          );
          toast.error(
            "An error occurred. Listing ID is missing for this step."
          );
          return; // Do not proceed with navigation
        }
        setStepCompleted(currentStep, true);
        setCurrentStep(nextStep);
        setValidateOnChange(false);
        // setFormTouched(false); // No longer needed as formTouched is derived
        navigate(
          `/dashboard/add-listing/edit/${currentDraftId}/step/${nextStep}`,
          {
            replace: true,
          }
        );
      }
    } catch (error: any) {
      // Catch specific error from saveCurrentStep or other errors
      // The toast for "Failed to save progress" is already shown in FormProvider's saveCurrentStep
      // So, we might not need another generic one here unless it's a different type of error.
      // If saveCurrentStep threw "Failed to create draft listing...", FormProvider already toasted.
      if (
        error &&
        error.message &&
        !error.message.includes("Failed to create draft listing")
      ) {
        toast.error(
          error.message || "An unexpected error occurred while navigating."
        );
      }
      console.error("[useFormNavigation] Error in handleNextStep:", error);
    }
  }, [
    currentStep,
    canProceedToNextStep,
    form,
    setStepCompleted,
    setCurrentStep,
    setValidateOnChange,
    // setFormTouched,
    saveCurrentStep,
    listingId, // Add listingId dependency
    navigate, // Add navigate dependency
  ]); // Removed setFormTouched from dependencies

  const handlePrevStep = useCallback(() => {
    console.log(
      "[useFormNavigation] handlePrevStep called. Current step:",
      FormStep[currentStep]
    );
    // When going back, disable validation - user is just navigating
    setValidateOnChange(false);
    // setFormTouched(false); // No longer needed as formTouched is derived
    setCurrentStep((currentStep - 1) as FormStep);
  }, [setValidateOnChange, setCurrentStep, currentStep]); // Removed setFormTouched from dependencies

  const handlePreview = useCallback(() => {
    toast.info("Preview functionality will be implemented in a future update.");
  }, []);

  // Skip validation for a specific step (useful in certain scenarios)
  const skipValidationAndGoToStep = useCallback(
    (step: FormStep) => {
      setValidateOnChange(false);
      // setFormTouched(false); // No longer needed as formTouched is derived
      setCurrentStep(step);
    },
    [setValidateOnChange, setCurrentStep] // Removed setFormTouched from dependencies
  );

  return {
    currentStep,
    canProceedToNextStep,
    handleNextStep,
    handlePrevStep,
    handlePreview,
    skipValidationAndGoToStep,
  };
};
